import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled, createTheme } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BarChartIcon from '@mui/icons-material/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import LayersIcon from '@mui/icons-material/Layers';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppProvider } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { BsGrid3X3GapFill } from "react-icons/bs";
import { HiUsers } from 'react-icons/hi2';
import { FaBasketShopping, FaFilePrescription } from "react-icons/fa6";
import Chart from 'chart.js/auto';
import { getAllRetrieveProperties } from '../Api';
import { BiSolidCoupon } from 'react-icons/bi';
import { FaFileAlt, FaLocationArrow } from 'react-icons/fa';

// Navigation configuration
const NAVIGATION = [
  {
    kind: 'header',
    title: 'Main items',
  },
  {
    segment: 'dashboard',
    title: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    segment: 'dashboard-products',
    title: 'Products',
    icon: <FaBasketShopping style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-users',
    title: 'Users',
    icon: <HiUsers style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-categories',
    title: 'Categories',
    icon: <BsGrid3X3GapFill style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-orders',
    title: 'Orders',
    icon: <ShoppingCartIcon />,
  },
  {
    segment: 'prescriptions',
    title: 'Prescriptions',
    icon: <FaFilePrescription style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-coupons',
    title: 'Coupons',
    icon: <BiSolidCoupon style={{ fontSize: '22px' }} />,
  },
  {
    segment: 'dashboard-content',
    title: 'Content',
    icon: <FaFileAlt style={{ fontSize: '20px' }} />,
  },
  {
    segment: 'dashboard-location',
    title: 'Location',
    icon: <FaLocationArrow />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    segment: 'reports',
    title: 'Reports',
    icon: <BarChartIcon />,
    children: [
      {
        segment: 'sales',
        title: 'Sales',
        icon: <DescriptionIcon />,
      },
      {
        segment: 'traffic',
        title: 'Traffic',
        icon: <DescriptionIcon />,
      },
    ],
  },
  {
    segment: 'integrations',
    title: 'Integrations',
    icon: <LayersIcon />,
  },
];

// Styled Skeleton component
const Skeleton = styled('div')(({ theme, height }) => ({
  backgroundColor: theme.palette.action.hover,
  borderRadius: theme.shape.borderRadius,
  height,
  content: '" "',
}));

// Custom router hook
function useDemoRouter(initialPath) {
  const [pathname, setPathname] = React.useState(initialPath);
  const navigate = useNavigate();

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path) => {
        setPathname(String(path));
        navigate(path);
      },
    };
  }, [pathname, navigate]);

  return router;
}

// Create a light theme
const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

function Sales({ onLogout }) {
  const navigate = useNavigate();
  const router = useDemoRouter('/reports/sales');
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState([]);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Fetch sales data
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (dateRange.start && dateRange.end) {
      setLoading(true);
      setError(null);
      try {
        // BUG-6: the date range was validated and then thrown away — this was
        // called with no parameters, so the report was identical regardless of
        // what the operator picked. WooCommerce's /reports/sales takes
        // date_min/date_max as YYYY-MM-DD, which is what <input type="date">
        // already produces.
        const data = await getAllRetrieveProperties('/sales', {
          date_min: dateRange.start,
          date_max: dateRange.end,
        });
        setSalesData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    } else {
      setError('Please select a valid date range.');
    }
  };

  // Initialize and update chart
  useEffect(() => {
    if (salesData.length > 0 && chartRef.current) {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      chartInstanceRef.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: salesData.map(item => Object.keys(item.totals)[0]),
          datasets: [
            {
              label: 'Total Sales ($)',
              data: salesData.map(item => parseFloat(item.total_sales)),
              backgroundColor: 'rgba(59, 130, 246, 0.6)',
              borderColor: '#3b82f6',
              borderWidth: 1,
            },
            {
              label: 'Total Orders',
              data: salesData.map(item => item.total_orders),
              backgroundColor: 'rgba(34, 197, 94, 0.6)',
              borderColor: '#22c55e',
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Daily Sales and Orders',
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Value',
              },
            },
            x: {
              title: {
                display: true,
                text: 'Date',
              },
            },
          },
        },
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [salesData]);

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    setOpenLogoutDialog(false);
    onLogout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setOpenLogoutDialog(false);
  };

  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  // Custom branding with logo
  const branding = {
    logo: (
      <div className="header-logo">
        <img onClick={() => navigate('/')} src={require('../images/logo.png')} alt="" style={{ height: '52px', width: '60px' }} />
      </div>
    ),
    title: '',
  };

  // Custom toolbar actions for the header
  const ToolbarActions = () => (
    <Button
      variant="outlined"
      startIcon={<LogoutIcon />}
      onClick={handleLogoutClick}
      sx={{ marginRight: 2 }}
      style={{ background: "#0165AB", color: "#fff" }}
    >
      Logout
    </Button>
  );

  return (
    <AppProvider
      navigation={NAVIGATION}
      router={router}
      theme={theme}
      branding={branding}
    >
      <DashboardLayout
        slots={{
          toolbarActions: ToolbarActions,
        }}
      >
        <PageContainer>
          {loading ? (
            <Grid container spacing={1}>
              <Grid size={5} />
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
              <Grid size={4}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={8}>
                <Skeleton height={100} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={150} />
              </Grid>
              <Grid size={12}>
                <Skeleton height={14} />
              </Grid>
            </Grid>
          ) : (
            <section className="admin-dashboard">
              <Grid container spacing={2}>
                <Grid size={12} height={5} />
                {/* Date Range Form */}
                <Grid size={12}>
                  <form onSubmit={handleSubmit} className="mb-6">
                    <Grid container spacing={2} alignItems="flex-end">
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <label className="block text-sm font-medium">Start Date</label>
                        <input
                          type="date"
                          value={dateRange.start}
                          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                          className="border p-2 rounded w-full"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <label className="block text-sm font-medium">End Date</label>
                        <input
                          type="date"
                          value={dateRange.end}
                          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                          className="border p-2 rounded w-full"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={loading}
                          style={{ background: "#0165AB", color: "#fff" }}
                        >
                          {loading ? 'Loading...' : 'Generate Report'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Grid>
                {/* Error Message */}
                {error && (
                  <Grid size={12}>
                    <p className="text-red-500 mb-4">{error}</p>
                  </Grid>
                )}
                {/* Chart */}
                {salesData.length > 0 && (
                  <Grid size={12}>
                    <h2 className="text-xl font-semibold mb-2">Sales and Orders Chart</h2>
                    <canvas id="salesChart" ref={chartRef}></canvas>
                  </Grid>
                )}
                {/* Sales Summary */}
                {salesData.length > 0 && (
                  <Grid size={12}>
                    <h2 className="text-xl font-semibold mb-2">Sales Summary</h2>
                    <p>Total Sales: ${salesData.reduce((sum, item) => sum + parseFloat(item.total_sales), 0).toFixed(2)}</p>
                    <p>Total Orders: {salesData.reduce((sum, item) => sum + item.total_orders, 0)}</p>
                    <p>Total Customers: {salesData.reduce((sum, item) => sum + item.total_customers, 0)}</p>
                    <p>Total Items: {salesData.reduce((sum, item) => sum + item.total_items, 0)}</p>
                    <p>Total Discounts: ${salesData.reduce((sum, item) => sum + parseFloat(item.total_discount), 0).toFixed(2)}</p>
                    <p>Total Shipping: ${salesData.reduce((sum, item) => sum + parseFloat(item.total_shipping), 0).toFixed(2)}</p>
                    <p>Total Tax: ${salesData.reduce((sum, item) => sum + parseFloat(item.total_tax), 0).toFixed(2)}</p>
                    <p>Total Refunds: {salesData.reduce((sum, item) => sum + item.total_refunds, 0)}</p>
                  </Grid>
                )}
              </Grid>
            </section>
          )}
        </PageContainer>
      </DashboardLayout>
      <Dialog
        open={openLogoutDialog}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} autoFocus>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </AppProvider>
  );
}

export default Sales;